import { useMemo, useState } from "react";
import { Layers2, ListFilter } from "lucide-react";
import {
  comparativoFinanceiroService,
  tipoCaixaService,
} from "../../services/apiService";
import { useQuery } from "../../hooks/useQuery";
import { useNotification } from "../../hooks/useNotification";
import PageContainer from "../../components/PageContainer";
import {
  FormField,
  SelectInput,
  TipoCaixaSelect,
  cardClass,
  tableCompactClass,
  tableEmptyStateClass,
  tableWrapperClass,
} from "../../components/ui";
import { formatCurrencyMoeda } from "../../utils/FormatarValores";

const MESES_OPCOES = [3, 6, 12, 24];

const getValorDespesa = (item) => {
  return Number(item?.valorDespesa ?? 0);
};

const getValorCaixa = (item) => {
  return Number(item?.valorTotal ?? 0);
};

const formatCurrencyAbs = (valor) => {
  return formatCurrencyMoeda(Math.abs(Number(valor || 0)));
};

const getMonthKey = (valor) => {
  if (!valor) {
    return "-";
  }

  if (typeof valor === "string") {
    const [, mes, ano] = valor.split("/");
    if (mes && ano) {
      return `${mes}/${ano}`;
    }
  }

  if (Array.isArray(valor) && valor.length >= 2) {
    const ano = String(valor[0]);
    const mes = String(valor[1]).padStart(2, "0");
    return `${mes}/${ano}`;
  }

  return String(valor);
};

const mesAnoParaTimestamp = (mesAno) => {
  if (!mesAno || !mesAno.includes("/")) {
    return 0;
  }

  const [mes, ano] = mesAno.split("/").map((item) => Number(item || 0));
  return new Date(ano, mes - 1, 1).getTime();
};

const ComparativoFinanceiro = () => {
  const { showError } = useNotification();
  const [qtdMeses, setQtdMeses] = useState(6);
  const [tipoCaixaId, setTipoCaixaId] = useState("");

  const { data: tiposCaixaData = [] } = useQuery(
    ["tipo-caixa-comparativo"],
    () => tipoCaixaService.listarTipoCaixa(),
    {
      onError: (err) => {
        showError(err.message || "Erro ao carregar tipos de caixa");
      },
    }
  );

  const tiposCaixa = useMemo(() => {
    if (!Array.isArray(tiposCaixaData)) {
      return [];
    }

    return tiposCaixaData;
  }, [tiposCaixaData]);

  // Sem tipo escolhido, o comparativo considera todos os tipos de caixa.
  const { data, isLoading } = useQuery(
    ["comparativo-financeiro", qtdMeses, tipoCaixaId],
    () => comparativoFinanceiroService.obterComparativo(qtdMeses, tipoCaixaId),
    {
      enabled: Boolean(qtdMeses),
      onError: (err) => {
        showError(err.message || "Erro ao carregar comparativo financeiro");
      },
    }
  );

  const despesas = useMemo(() => {
    return Array.isArray(data?.despesas) ? data.despesas : [];
  }, [data]);

  const caixas = useMemo(() => {
    return Array.isArray(data?.caixas) ? data.caixas : [];
  }, [data]);

  const totalDespesas = useMemo(() => {
    return despesas.reduce((acc, item) => acc + getValorDespesa(item), 0);
  }, [despesas]);

  const totalCaixas = useMemo(() => {
    return caixas.reduce((acc, item) => acc + getValorCaixa(item), 0);
  }, [caixas]);

  const totalDiferenca = totalCaixas - totalDespesas;

  const relatorio = useMemo(() => {
    const mapa = new Map();

    despesas.forEach((item) => {
      const mes = getMonthKey(item?.dataVencimento);
      const atual = mapa.get(mes) || { mes, despesas: 0, entrada: 0, diferenca: 0 };
      atual.despesas += getValorDespesa(item);
      mapa.set(mes, atual);
    });

    caixas.forEach((item) => {
      const mes = getMonthKey(item?.data);
      const atual = mapa.get(mes) || { mes, despesas: 0, entrada: 0, diferenca: 0 };
      atual.entrada += getValorCaixa(item);
      mapa.set(mes, atual);
    });

    return Array.from(mapa.values())
      .map((item) => ({
        ...item,
        diferenca: item.entrada - item.despesas,
      }))
      .sort((a, b) => mesAnoParaTimestamp(a.mes) - mesAnoParaTimestamp(b.mes));
  }, [caixas, despesas]);

  return (
    <div className="min-h-full bg-slate-100 p-6 md:p-8">
      <PageContainer className="space-y-6">
        <div className={`${cardClass} p-5`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Quantidade de meses" htmlFor="comparativo-qtd-meses">
              <div className="relative">
                <ListFilter
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <SelectInput
                  id="comparativo-qtd-meses"
                  value={qtdMeses}
                  onChange={(e) => setQtdMeses(Number(e.target.value))}
                  className="rounded-md border-gray-300 pl-10 pr-3"
                >
                  {MESES_OPCOES.map((meses) => (
                    <option key={meses} value={meses}>
                      {meses} meses
                    </option>
                  ))}
                </SelectInput>
              </div>
            </FormField>

            <FormField label="Tipo de Caixa" htmlFor="comparativo-tipo-caixa">
              <div className="relative">
                <Layers2
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <TipoCaixaSelect
                  id="comparativo-tipo-caixa"
                  tiposCaixa={tiposCaixa}
                  value={tipoCaixaId}
                  onChange={(e) => setTipoCaixaId(e.target.value)}
                  className="rounded-md border-gray-300 pl-10 pr-3"
                  placeholder="Todos os tipos"
                />
              </div>
            </FormField>
          </div>
        </div>

        <div className={`${cardClass} overflow-hidden`}>
          <div className={tableWrapperClass}>
            <table className={tableCompactClass}>
              <thead>
                <tr className="bg-slate-50 text-slate-600 border-b border-slate-200">
                  <th className="text-left px-6 py-4 font-semibold">Mês de Referência</th>
                  <th className="text-right px-6 py-4 font-semibold">
                    Valor Despesas até dia 15
                  </th>
                  <th className="text-right px-6 py-4 font-semibold">
                    Entrada Clínica até dia 15
                  </th>
                  <th className="text-right px-6 py-4 font-semibold">Diferença</th>
                </tr>
              </thead>

              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={4} className={tableEmptyStateClass}>
                      Carregando comparativo financeiro...
                    </td>
                  </tr>
                )}

                {!isLoading && relatorio.length === 0 && (
                  <tr>
                    <td colSpan={4} className={tableEmptyStateClass}>
                      Nenhum registro encontrado para os filtros selecionados.
                    </td>
                  </tr>
                )}

                {relatorio.map((item) => (
                  <tr
                    key={item.mes}
                    className="border-b border-slate-100 hover:bg-slate-50 transition"
                  >
                    <td className="px-6 py-5 text-left font-semibold text-slate-700">
                      {item.mes}
                    </td>

                    <td className="px-6 py-5 text-right text-rose-500 font-medium">
                      {formatCurrencyMoeda(item.despesas)}
                    </td>

                    <td className="px-6 py-5 text-right text-slate-700 font-medium">
                      {formatCurrencyMoeda(item.entrada)}
                    </td>

                    <td
                      className={`px-6 py-5 text-right font-bold ${
                        item.diferenca >= 0 ? "text-emerald-600" : "text-rose-500"
                      }`}
                    >
                      {formatCurrencyAbs(item.diferenca)}
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr className="bg-gray-100  text-slate-800 font-semibold">
                  <td className="px-6 py-5">Total</td>
                  <td className="px-6 py-5 text-right">{formatCurrencyMoeda(totalDespesas)}</td>
                  <td className="px-6 py-5 text-right">{formatCurrencyMoeda(totalCaixas)}</td>
                  <td
                    className={`px-6 py-5 text-right ${
                      totalDiferenca >= 0 ? "text-emerald-600" : "text-rose-500"
                    }`}
                  >
                    {formatCurrencyAbs(totalDiferenca)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default ComparativoFinanceiro;
